import "@/App.css";
import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";

import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";

// Chargement différé (code-splitting) de toutes les pages sauf Login.
// -----------------------------------------------------------------------
// Pourquoi : auparavant, les ~40 pages de l'application étaient importées
// en haut de ce fichier (import statique), ce qui oblige le navigateur à
// télécharger ET exécuter le code JavaScript de TOUTES les pages (plannings,
// RH, facturation, lavage de vitres...) avant même d'afficher le tableau de
// bord — un seul fichier main.js énorme. C'est la cause principale du
// ressenti "l'application met du temps à s'afficher au démarrage et en
// changeant de page" : React ne peut commencer à dessiner la page demandée
// qu'une fois tout ce code (utile ou non à cette page précise) chargé et
// interprété par le navigateur.
// React.lazy() + Suspense découpe le bundle en un petit fichier par page :
// seul le code de la page réellement affichée est téléchargé, au moment où
// on y navigue (les suivants sont mis en cache par le navigateur). Le
// contenu affiché est strictement identique, seul le moment de son
// chargement change.
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Employees = lazy(() => import("@/pages/Employees"));
const EmployeeDetail = lazy(() => import("@/pages/EmployeeDetail"));
const EmployeeForm = lazy(() => import("@/pages/EmployeeForm"));
const Users = lazy(() => import("@/pages/Users"));
const Clients = lazy(() => import("@/pages/Clients"));
const ClientsLost = lazy(() => import("@/pages/ClientsLost"));
const Absences = lazy(() => import("@/pages/Absences"));
const Payroll = lazy(() => import("@/pages/Payroll"));
const Planning = lazy(() => import("@/pages/Planning"));
const PlanningWeek = lazy(() => import("@/pages/PlanningWeek"));
const PlanningEmployee = lazy(() => import("@/pages/PlanningEmployee"));
const Revenue = lazy(() => import("@/pages/Revenue"));
const RevenueEmployeeDetail = lazy(() => import("@/pages/RevenueEmployeeDetail"));
const Profitability = lazy(() => import("@/pages/Profitability"));
const ChargesSalaires = lazy(() => import("@/pages/ChargesSalaires"));
const ChargesSuivi = lazy(() => import("@/pages/ChargesSuivi"));
const ChargesTva = lazy(() => import("@/pages/ChargesTva"));
const Vehicles = lazy(() => import("@/pages/Vehicles"));
const Assignments = lazy(() => import("@/pages/Assignments"));
const PlanningBoard = lazy(() => import("@/pages/PlanningBoard"));
const Hours = lazy(() => import("@/pages/Hours"));
const Replacements = lazy(() => import("@/pages/Replacements"));
const Closures = lazy(() => import("@/pages/Closures"));
const Medical = lazy(() => import("@/pages/Medical"));
const HrDocuments = lazy(() => import("@/pages/HrDocuments"));
const Disciplinary = lazy(() => import("@/pages/Disciplinary"));
const Lifecycle = lazy(() => import("@/pages/Lifecycle"));
const Alerts = lazy(() => import("@/pages/Alerts"));
const Checklist = lazy(() => import("@/pages/Checklist"));
const Agenda = lazy(() => import("@/pages/Agenda"));
const Fuel = lazy(() => import("@/pages/Fuel"));
const WindowCleaning = lazy(() => import("@/pages/WindowCleaning"));
const WindowCleaningReports = lazy(() => import("@/pages/WindowCleaningReports"));
const CleaningTracking = lazy(() => import("@/pages/CleaningTracking"));
const Checks = lazy(() => import("@/pages/Checks"));
const CleaningTrackingReports = lazy(() => import("@/pages/CleaningTrackingReports"));

// Petit indicateur de chargement affiché le temps que le code de la page
// demandée soit téléchargé (quelques centaines de ms en général, une seule
// fois par page puis mis en cache navigateur).
function PageLoading() {
  return (
    <div className="flex items-center justify-center py-24 text-gray-400 text-sm">
      Chargement…
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" />
        <Suspense fallback={<PageLoading />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/employees" element={<Employees />} />
              <Route path="/employees/new" element={<ProtectedRoute roles={["admin_rh"]}><EmployeeForm /></ProtectedRoute>} />
              <Route path="/employees/:id" element={<EmployeeDetail />} />
              <Route path="/employees/:id/edit" element={<ProtectedRoute roles={["admin_rh"]}><EmployeeForm /></ProtectedRoute>} />
              <Route path="/clients" element={<ProtectedRoute roles={["admin_rh"]}><Clients /></ProtectedRoute>} />
              <Route path="/clients-lost" element={<ProtectedRoute roles={["admin_rh"]}><ClientsLost /></ProtectedRoute>} />
              <Route path="/absences" element={<Absences />} />
              <Route path="/payroll" element={<ProtectedRoute roles={["admin_rh"]}><Payroll /></ProtectedRoute>} />
              <Route path="/planning" element={<Planning />} />
              <Route path="/planning-semaine" element={<PlanningWeek />} />
              <Route path="/planning/board" element={<PlanningBoard />} />
              <Route path="/planning/:employeeId" element={<PlanningEmployee />} />
              <Route path="/assignments" element={<Assignments />} />
              <Route path="/hours" element={<ProtectedRoute roles={["admin_rh"]}><Hours /></ProtectedRoute>} />
              <Route path="/replacements" element={<Replacements />} />
              <Route path="/closures" element={<Closures />} />
              <Route path="/medical" element={<Medical />} />
              <Route path="/hr-documents" element={<HrDocuments />} />
              <Route path="/disciplinary" element={<ProtectedRoute roles={["admin_rh"]}><Disciplinary /></ProtectedRoute>} />
              <Route path="/lifecycle" element={<ProtectedRoute roles={["admin_rh"]}><Lifecycle /></ProtectedRoute>} />
              <Route path="/alerts" element={<Alerts />} />
              <Route path="/revenue" element={<ProtectedRoute roles={["admin_rh"]}><Revenue /></ProtectedRoute>} />
              <Route path="/revenue/:employeeId" element={<ProtectedRoute roles={["admin_rh"]}><RevenueEmployeeDetail /></ProtectedRoute>} />
              <Route path="/profitability" element={<ProtectedRoute roles={["admin_rh"]}><Profitability /></ProtectedRoute>} />
              <Route path="/charges/salaires" element={<ProtectedRoute roles={["admin_rh"]}><ChargesSalaires /></ProtectedRoute>} />
              <Route path="/charges/suivi" element={<ProtectedRoute roles={["admin_rh"]}><ChargesSuivi /></ProtectedRoute>} />
              <Route path="/charges/tva" element={<ProtectedRoute roles={["admin_rh"]}><ChargesTva /></ProtectedRoute>} />
              <Route path="/vehicles" element={<Vehicles />} />
              <Route path="/checklist" element={<Checklist />} />
              <Route path="/checks" element={<Checks />} />
              <Route path="/agenda" element={<Agenda />} />
              <Route path="/fuel" element={<ProtectedRoute roles={["admin_rh", "manager"]}><Fuel /></ProtectedRoute>} />
              <Route path="/window-cleaning" element={<WindowCleaning />} />
              <Route path="/window-cleaning/reports" element={<WindowCleaningReports />} />
              <Route path="/cleaning-tracking" element={<CleaningTracking />} />
              <Route path="/cleaning-tracking/reports" element={<CleaningTrackingReports />} />
              <Route path="/users" element={<ProtectedRoute roles={["admin_rh"]}><Users /></ProtectedRoute>} />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
